import { Controller } from '@hotwired/stimulus'
import { useStore } from 'stimulus-store'
import { deviceTypeStore } from "../stores/device_types/device_type_store";
import { allDeviceTypesStore } from "../stores/device_types/all_device_types_store";
import { manufacturerStore } from '../stores/device_types/manufacturer_store'
import { modelStore } from '../stores/device_types/model_store'
import { bundleStore } from '../stores/device_types/bundle_store'
import { getModels } from 'shared/device_types/device_types'
import { multiDeviceTypeStore } from "../stores/device_types/multi_device_type_store";
import { getDeviceTypeId, getManufacturerId, getModelId } from "shared/device_types/device_type_id_helpers";

/**
 * Walkthrough Device Type Palette Controller
 *
 * Handles device type and model selection on the Walkthrough page.
 * Integrates with stimulus-store for reactive updates and mutual exclusivity
 * with bundle selections.
 *
 * TURBO DRIVE NOTE: The page-specific stores used here (deviceTypeStore,
 * manufacturerStore, modelStore, bundleStore) are module-level singletons that
 * persist across Turbo navigations. Locale stores are cleared by their own
 * select controllers' disconnect() methods when leaving the previous page
 * (see region_select_controller.js), but these device-type stores are cleared
 * via clearDeviceTypeStores() when the user interacts. If stale values from a
 * previous navigation cause issues, consider clearing them in connect().
 *
 * Requirements: 5.1, 5.2, 5.4, 5.5
 */

// Connects to data-controller="forms--walkthrough-device-type-palette"
export default class extends Controller {

    static stores = [
        multiDeviceTypeStore,
        deviceTypeStore,
        allDeviceTypesStore,
        manufacturerStore,
        modelStore,
        bundleStore
    ]

    connect() {
        useStore(this);
    }

    /**
     * React to multi-device type store changes
     * Fetch and display models for all selected device types
     * Requirements: 5.1, 5.4
     */
    onMultiDeviceTypeStoreUpdate(newValue) {
        /*
        The newValue will be an array of device type objects like:
        [
          {
            "id":"2557aabd-8298-4b4d-9922-2be4e1393e13",
            "name":"Printer",
            "icon":"icon-printer",
            "printer":true,"workstation":false
          },
          {
            "id":"3668bbce-9399-5c5e-a033-3cf5f2404f24",
            "name":"Workstation",
            "icon":"icon-desktop",
            "printer":false,"workstation":true
          }
        ]
        Or empty array [] when no device types are selected
         */

        if (!newValue || newValue.length === 0) {
            // Clear manufacturer and model stores when no device types are selected
            this.clearDeviceTypeManufacturerAndModelStores();

            // Clear the device palette content
            this.clearDevicePaletteContent();
        } else {
            // Fetch and display models for all selected device types
            this.fetchAndDisplayModelsForAllDeviceTypes(newValue);
        }
    }

    /**
     * React to bundle store changes
     * Clear device type stores when bundle is selected to ensure mutual exclusivity
     * Requirements: 5.1, 5.4, 5.5
     */
    onBundleStoreUpdate(newValue) {
        // If a bundle is selected, clear device type stores to prevent conflicts
        if (newValue && (newValue.id || Object.keys(newValue).length > 0)) {
            this.clearDeviceTypeManufacturerAndModelStores();
        }
    }

    /**
     * Handle model button selection
     * Parse encoded button values and update stores
     * Requirements: 5.2
     */
    modelSelected(event) {
        // Enable the default action. This highlights the selected button. This is
        // okay for us to do here since we don't rely on the standard event actions
        // to trigger behavior. We use the stimulus-store listener methods.
        // event.preventDefault();

        const button = event.currentTarget;
        const encodedValue = button.dataset.modelInfo;

        // Return an error if the button doesn't have the data we need
        if (!encodedValue) {
            console.error('Model button missing data-model-info attribute')
            return
        }

        // Parse out the device type, manufacturer, and model IDs:
        const deviceTypeId = getDeviceTypeId(encodedValue);
        const manufacturerId = getManufacturerId(encodedValue);
        const modelId = getModelId(encodedValue);

        // Find the full device type definition in allDeviceTypesStore
        const selectedDeviceType = this.allDeviceTypesStoreValue.find(deviceType => deviceType.id === deviceTypeId);

        // Store the entire device type definition (includes icon and other properties)
        this.setDeviceTypeStoreValue(selectedDeviceType);

        // Update manufacturer and model stores with parsed values
        this.setManufacturerStoreValue({id: manufacturerId});
        this.setModelStoreValue({id: modelId});

        // Show device-specific form fields for individual device creation
        this.showDeviceFormForDevices();
    }

    /**
     * Clear selected device types from the multi-select dropdown
     * Called when bundle tab is clicked to ensure mutual exclusivity
     * Requirements: 5.5
     */
    clearSelected() {
        // Find the device type select element and clear its selection
        const deviceTypeSelect = document.querySelector('#device_types_select');
        if (deviceTypeSelect && deviceTypeSelect.tomselect) {
            deviceTypeSelect.tomselect.clear();
        } else {
            console.warn('Device type select element or TomSelect instance not found');
        }

        // Also clear the device palette content and radio buttons
        this.clearDevicePaletteContent();
    }

    /**
     * Clear manufacturer and model stores
     * Called when device type changes or bundle is selected
     * Requirements: 5.4, 5.5
     */
    clearDeviceTypeManufacturerAndModelStores() {
        try {
            // Clear the button value stores
            this.setDeviceTypeStoreValue({});
            this.setManufacturerStoreValue({});
            this.setModelStoreValue({});
        } catch (error) {
            // console.error('Error clearing device type, manufacturer, or model selection:', error);
            // Intentionally left blank
        }
    }

    /**
     * Clear the device palette content area
     * Requirements: 5.1, 5.4
     */
    clearDevicePaletteContent() {
        // Clear the selected device type store
        // this.setMultiDeviceTypeStoreValue([]);

        // TODO: REPLACE THIS WITH A TARGET
        const devicePaletteContent = document.querySelector('.device-palette-content')
        if (devicePaletteContent) {
            // First, unselect any selected radio buttons before clearing content
            this.clearDevicePaletteRadioButtons(devicePaletteContent);

            // Then clear the HTML content
            devicePaletteContent.innerHTML = '<p class="text-muted">Select device types above to see available models</p>'
        }

        this.clearDeviceTypeManufacturerAndModelStores();
    }

    /**
     * Clear/unselect all radio buttons in the device palette content area
     * Requirements: 5.1, 5.4
     */
    clearDevicePaletteRadioButtons(contentArea = null) {
        // Use provided content area or find it
        const devicePaletteContent = contentArea || document.querySelector('.device-palette-content');

        if (devicePaletteContent) {
            // Find all radio buttons in the device palette content
            const radioButtons = devicePaletteContent.querySelectorAll('input[type="radio"][name="device_types"]');

            radioButtons.forEach(radioButton => {
                if (radioButton.checked) {
                    radioButton.checked = false;
                }
            });
        }
    }

    /**
     * Fetch and display models for all selected device types
     * Requirements: 5.1, 5.2
     */
    async fetchAndDisplayModelsForAllDeviceTypes(deviceTypes) {
        try {
            // Clear existing content first
            this.clearDevicePaletteContent();

            // Show loading state
            const devicePaletteContent = document.querySelector('.device-palette-content')
            if (devicePaletteContent) {
                devicePaletteContent.innerHTML = '<p class="text-muted">Loading models...</p>'
            }

            // Fetch models for all device types concurrently
            const modelPromises = deviceTypes.map(deviceType =>
                getModels(deviceType.id, null).then(models => ({
                    deviceType: deviceType,
                    models: models
                }))
            );

            const deviceTypeModels = await Promise.all(modelPromises);

            // Generate HTML for all device types and their models
            let allModelsHtml = '';
            let totalModels = 0;

            deviceTypeModels.forEach(({models}) => {
                if (models && models.length > 0) {
                    // Add device type header
                    allModelsHtml += this.generateModelRadioButtons(models);
                    totalModels += models.length;
                }
            });

            // Update the device palette content
            if (devicePaletteContent) {
                if (totalModels > 0) {
                    devicePaletteContent.innerHTML = allModelsHtml;
                } else {
                    devicePaletteContent.innerHTML = '<p class="text-muted">No models found for the selected device types</p>';
                }
            }
        } catch (error) {
            console.error('Error fetching models for device types:', error);

            // Show error state
            const devicePaletteContent = document.querySelector('.device-palette-content');
            if (devicePaletteContent) {
                devicePaletteContent.innerHTML = '<p class="text-danger">Error loading models. Please try again.</p>';
            }
        }
    }

    /**
     * Generate HTML for model radio buttons
     * Requirements: 5.2
     */
    generateModelRadioButtons(models) {
        if (!models || models.length === 0) {
            return '<p class="text-muted">No models available</p>'
        }

        // Sort models alphabetically by name
        const sortedModels = models.sort((a, b) => a.name.localeCompare(b.name));

        return sortedModels.map(model => {
            // Create encoded value in format "device_type_id!manufacturer_id!model_id"
            // For now, we'll use the model.id for both manufacturer and model since we don't have separate manufacturer info
            const encodedValue = `${model.device_type}!${model.manufacturer}!${model.model}`
            const radioId = model.model

            return (` 
                <input class="btn-check" 
                       type="radio" 
                       name="device_types" 
                       id="${radioId}" 
                       value="${encodedValue}"
                       data-action="click->walkthrough-device-type-palette#modelSelected"
                       data-model-info="${encodedValue}"
                       autocomplete="off" />
                <label class="btn btn-outline-secondary" for="${radioId}">${this.escapeHtml(model.name)}</label>
            `)
        }).join('')
    }

    /**
     * Escape HTML to prevent XSS
     */
    escapeHtml(text) {
        const div = document.createElement('div')
        div.textContent = text
        return div.innerHTML
    }

    showDeviceFormForDevices() {
        // Show the name, port id, dns name, IP address, and mac address fields
        const nameField = document.getElementById('walkthrough_name');
        const portIdField = document.getElementById('walkthrough_port_id');
        const dnsNameField = document.getElementById('walkthrough_dns_name');
        const ipAddressField = document.getElementById('walkthrough_ip_address');
        const macAddressField = document.getElementById('walkthrough_mac_address');

        // Show name field
        if (nameField) {
            const nameGroup = nameField.closest('.form-group, .mb-3, .field');
            if (nameGroup) {
                nameGroup.style.display = 'block';
            }
        }

        // Show port ID field
        if (portIdField) {
            const portGroup = portIdField.closest('.form-group, .mb-3, .field');
            if (portGroup) {
                portGroup.style.display = 'block';
            }
        }

        // Show DNS name field
        if (dnsNameField) {
            const dnsGroup = dnsNameField.closest('.form-group, .mb-3, .field');
            if (dnsGroup) {
                dnsGroup.style.display = 'block';
            }
        }

        // Show IP address field
        if (ipAddressField) {
            const ipGroup = ipAddressField.closest('.form-group, .mb-3, .field');
            if (ipGroup) {
                ipGroup.style.display = 'block';
            }
        }

        // Show MAC address field
        if (macAddressField) {
            const macGroup = macAddressField.closest('.form-group, .mb-3, .field');
            if (macGroup) {
                macGroup.style.display = 'block';
            }
        }
    }
}